import { type Common } from './common.js';
import {
  type BaseGroup,
  type FindAllOptions,
  type Group,
  type GroupPermission,
  type GroupWithPermissions,
  type Permission,
  type UpdateValues,
  type WithPermissionFindAllOptions,
} from './models/group.js';
import { type PaginatedQueryOptions } from './models/pagination/PaginatedQueryOptions.type.js';
import { type PaginationResponse } from './models/pagination/PaginationResponse.type.js';
import { type User } from './models/user.js';

export class API {
  baseEndpoint = '/groups';
  common: Common;

  constructor(common: Common) {
    this.common = common;
  }

  async findAll(options: FindAllOptions = {}): Promise<Group[]> {
    const url = this.common.constructPath(this.baseEndpoint, options);
    return await this.common.getJSON(url);
  }

  async findAllPaginated(
    options: PaginatedQueryOptions<FindAllOptions>
  ): Promise<PaginationResponse<Group>> {
    const url = this.common.constructPath(this.baseEndpoint, options);
    return await this.common.getJSON(url);
  }

  async findAllWithPermissionsPaginated(
    options: PaginatedQueryOptions<WithPermissionFindAllOptions>
  ): Promise<PaginationResponse<GroupWithPermissions>> {
    const url = this.common.constructPath(this.baseEndpoint, {
      ...options,
      withPermissions: true,
    });
    return await this.common.getJSON(url);
  }

  async findAllPermissions(): Promise<Permission[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/permissions`);
    return await this.common.getJSON(url);
  }

  async findAllWithPermissions(): Promise<GroupWithPermissions[]> {
    const url = this.common.constructPath(this.baseEndpoint, {
      withPermissions: true,
    });
    return await this.common.getJSON(url);
  }

  async findOneById(id: string): Promise<Group> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.getJSON(url);
  }

  async findOneWithPermissionsById(id: string): Promise<GroupWithPermissions> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`, {
      withPermissions: true,
    });
    return await this.common.getJSON(url);
  }

  async create(toAdd: BaseGroup): Promise<Group> {
    const url = this.common.constructPath(this.baseEndpoint);
    return await this.common.postJSON(url, toAdd);
  }

  async update(id: string, toUpdate: UpdateValues): Promise<Group> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.patchJSON(url, toUpdate);
  }

  async remove(id: string): Promise<boolean> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    const { success } = await this.common.deleteJSON(url);
    return success;
  }

  async findAllUsers(groupId: string): Promise<User[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${groupId}/users`);
    return await this.common.getJSON(url);
  }

  async createPermission(groupId: string, permission: Permission): Promise<GroupPermission> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${groupId}/permissions`);
    return await this.common.postJSON(url, { name: permission });
  }

  async removePermission(groupId: string, permissionId: string): Promise<void> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/${groupId}/permissions/${permissionId}`
    );
    const { success } = await this.common.deleteJSON(url);
    return success;
  }
}
